import Link from "next/link";
import Image from "next/image";
import { ScrollShadow } from "@nextui-org/react";

/**
 * News List Widget
 * A flexible news list component with thumbnails
 * 
 * @param {Array} items - Array of news items with {id, title, image, link, category, description, date}
 * @param {string} width - Container width (e.g., "400px", "100%", "w-full"). Defaults to "w-full" (parent width)
 * @param {string} height - Container height (e.g., "600px", "auto"). Defaults to "auto"
 * @param {string} maxWidth - Maximum width (e.g., "max-w-md", "max-w-lg"). Defaults to no max-width
 * @param {boolean} useScroll - Enable ScrollShadow for scrollable content. Defaults to false
 * @param {string} scrollHeight - Height of scrollable area (e.g., "400px", "500px"). Only applies when useScroll is true
 * @param {string} className - Additional CSS classes
 */
export default function NewsList({
    items = [],
    width = "w-full",
    height = "auto",
    maxWidth = "",
    useScroll = false,
    scrollHeight = "400px",
    className = ""
}) {
    // Build container classes
    const containerClasses = [
        typeof width === 'string' && width.startsWith('w-') ? width : '',
        maxWidth,
        "bg-white rounded-lg shadow-lg overflow-hidden",
        className
    ].filter(Boolean).join(' ');

    // Build inline styles for custom width/height
    const containerStyles = {};
    if (typeof width === 'string' && !width.startsWith('w-')) {
        containerStyles.width = width;
    }
    if (height !== 'auto') {
        containerStyles.height = height;
    }

    return (
        <div
            className={containerClasses}
            style={Object.keys(containerStyles).length > 0 ? containerStyles : undefined}
        >
            {/* Header */}
            <div className="bg-primary px-6 py-4">
                <h2 className="text-white text-xl font-bold text-center uppercase tracking-wide">
                    TIN MỚI NHẤT
                </h2>
            </div>

            {/* News List */}
            {useScroll ? (
                <ScrollShadow
                    hideScrollBar
                    className="p-3"
                    style={{ height: scrollHeight }}
                >
                    <div className="space-y-2.5">
                        {items.map((news, index) => (
                            <Link
                                href={news.link}
                                key={news.id}
                                className="group block"
                            >
                                <div className={`flex gap-3 items-start ${index < items.length - 1 ? 'pb-2.5 border-b border-gray-200' : ''}`}>
                                    {/* Thumbnail - Smaller size */}
                                    <div className="relative w-24 h-16 flex-shrink-0 rounded-lg overflow-hidden">
                                        <Image
                                            src={news.image}
                                            alt={news.title}
                                            fill
                                            className="object-cover group-hover:scale-105 transition-transform duration-300"
                                        />
                                    </div>

                                    {/* Title - Truncate to match image height */}
                                    <div className="flex-1 min-w-0 h-16 flex items-start">
                                        <h3 className="text-gray-800 text-sm font-semibold leading-tight group-hover:text-blue-600 transition-colors line-clamp-3 overflow-hidden">
                                            {news.title}
                                        </h3>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </ScrollShadow>
            ) : (
                <div className="p-3 space-y-2.5">
                    {items.map((news, index) => (
                        <Link
                            href={news.link}
                            key={news.id}
                            className="group block"
                        >
                            <div className={`flex gap-3 items-start ${index < items.length - 1 ? 'pb-2.5 border-b border-gray-200' : ''}`}>
                                {/* Thumbnail - Smaller size */}
                                <div className="relative w-24 h-16 flex-shrink-0 rounded-lg overflow-hidden">
                                    <Image
                                        src={news.image}
                                        alt={news.title}
                                        fill
                                        className="object-cover group-hover:scale-105 transition-transform duration-300"
                                    />
                                </div>

                                {/* Title - Truncate to match image height */}
                                <div className="flex-1 min-w-0 h-16 flex items-start">
                                    <h3 className="text-gray-800 text-sm font-semibold leading-tight group-hover:text-blue-600 transition-colors line-clamp-3 overflow-hidden">
                                        {news.title}
                                    </h3>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
